/**
 * 多线路：桌面端 / 手机 App 内置真实服务器列表，自动探测选线、失效切换。
 *
 * 用户不该知道「服务器地址」这回事 —— 腾讯会议从来不让人填。列表内置在
 * 应用里，启动时并发探测 /healthz，按**顺序优先**取第一条通的（备份语义：
 * 主线路能用就用主线路，只有它挂了才去备用，不按快慢挑 —— 否则同一个班的人
 * 会被分到不同服务器上，互相看不见）。
 *
 * 列表可以被服务端下发的 /servers.json 更新并缓存，新增备机不必发新版本。
 */

export interface ServerInfo {
  id: string
  name: string
  /** 形如 https://live.whaty.org，无结尾斜杠 */
  base: string
}

export interface ProbeResult {
  id: string
  ok: boolean
  /** 毫秒；失败时为 -1 */
  rtt: number
  /** 该线路的负载快照；读不到时为 undefined（见 probe 的注释）。 */
  load?: ServerLoad
}

/**
 * 服务端 /healthz 报出的负载快照。
 *
 * 用途是「这条线还有没有容量」：此前选线只知道通不通，主线路房间数打满时
 * 客户端仍会往上撞，要等进房报错才知道。
 *
 * 三个字段都可能读不到（见 probe 里 no-cors 的说明），因此调用方必须能在
 * load 为 undefined 时正常工作 —— 缺容量信息只是回到「只看通不通」的老行为，
 * 不能变成选不出线路。
 */
export interface ServerLoad {
  rooms: number
  users: number
  maxRooms: number
}

/** 房间数占到上限的这个比例即视为满载，选线时让位给下一条。 */
export const LOAD_FULL_RATIO = 0.95

function isServerLoad(x: unknown): x is ServerLoad {
  const o = x as ServerLoad
  return !!o && typeof o.rooms === 'number' && typeof o.users === 'number'
    && typeof o.maxRooms === 'number' && o.maxRooms > 0
}

/**
 * 这条线路是否已满载。读不到负载时返回 false —— 未知不等于满，
 * 把未知当满会在服务端还没升级、或跨域读不到 body 时把所有线路判死。
 */
export function isSaturated(load?: ServerLoad): boolean {
  // 显式校验字段齐全，不靠「undefined 参与比较恒为 false」兜着 —— 那样字段缺失
  // 会静默跳过容量判断，看起来一切正常，实际上判满这段代码从来没生效。
  if (!isServerLoad(load)) return false
  return load.rooms >= load.maxRooms * LOAD_FULL_RATIO
}

export const BUILTIN_SERVERS: ReadonlyArray<ServerInfo> = [
  { id: 'lax02', name: '主线路', base: 'https://live.whaty.org' },
  { id: 'kc01', name: '备用线路', base: 'https://live2.whaty.org' },
]

const KEY_LIST = 'whatylive.servers'
const KEY_CURRENT = 'whatylive.server'
const KEY_PINNED = 'whatylive.server.pinned'
/** 探测超时。手机网络下 4 秒会误判主线路不通、把同一个班的人分到两台服务器上，放宽到 6 秒。 */
export const PROBE_TIMEOUT_MS = 6000

function read(key: string): string | null {
  try { return localStorage.getItem(key) } catch { return null }
}
function write(key: string, v: string | null): void {
  try { v === null ? localStorage.removeItem(key) : localStorage.setItem(key, v) } catch { /* 隐私模式 */ }
}

function isServerInfo(x: unknown): x is ServerInfo {
  const o = x as ServerInfo
  return !!o && typeof o.id === 'string' && typeof o.name === 'string' && /^https?:\/\/[^/]+$/.test(o.base ?? '')
}

/** 当前线路列表：服务端下发并缓存的优先，否则内置。 */
export function serverList(): ServerInfo[] {
  try {
    const cached = JSON.parse(read(KEY_LIST) ?? 'null')
    if (Array.isArray(cached) && cached.length > 0 && cached.every(isServerInfo)) return cached
  } catch { /* 缓存坏了就当没有 */ }
  return [...BUILTIN_SERVERS]
}

export function setServerList(list: unknown): boolean {
  if (!Array.isArray(list) || list.length === 0 || !list.every(isServerInfo)) return false
  write(KEY_LIST, JSON.stringify(list))
  return true
}

/** 当前选中的线路；没选过时取列表第一条，保证启动就能发请求。 */
export function currentServer(): ServerInfo {
  const list = serverList()
  return list.find((s) => s.id === read(KEY_CURRENT)) ?? list[0]
}
export function setCurrentServer(id: string): void { write(KEY_CURRENT, id) }

/** 用户手动钉住的线路；null 表示自动。 */
export function pinnedServer(): string | null { return read(KEY_PINNED) }
export function pinServer(id: string | null): void { write(KEY_PINNED, id) }

/**
 * 探一条线路：/healthz 在超时内有应答才算通。
 * 用 no-cors：探测只关心「服务器活没活着」，不该受页面来源影响 ——
 * 本地联调时页面来源是 localhost:5173，带 CORS 的请求会被浏览器拦下，
 * 看起来像所有线路都挂了，其实只是没回跨域头。no-cors 下响应是 opaque
 * （读不到状态码），但 fetch 能 resolve 就说明 TCP/TLS/HTTP 全通了。
 */
export async function probe(s: ServerInfo, timeoutMs = PROBE_TIMEOUT_MS): Promise<ProbeResult> {
  const ctl = new AbortController()
  const timer = setTimeout(() => ctl.abort(), timeoutMs)
  const t0 = performance.now()
  const url = `${s.base}/healthz`
  try {
    // 先按可读方式请求：/healthz 的响应体带房间数与上限（routes.go 的 handleHealth），
    // 拿到就能在选线时避开已经打满的线路。桌面端来源在服务端 desktopOrigins 白名单里
    // （cors.go），Web 端同源，这两种情况都读得到。
    const r = await fetch(url, { signal: ctl.signal, cache: 'no-store', mode: 'cors' })
    const rtt = Math.round(performance.now() - t0)
    if (r.ok) {
      let load: ServerLoad | undefined
      try {
        const j: unknown = await r.json()
        if (isServerLoad(j)) load = { rooms: j.rooms, users: j.users, maxRooms: j.maxRooms }
      } catch { /* 老版本服务端没这几个字段，退回「只看通不通」 */ }
      return { id: s.id, ok: true, rtt, load }
    }
    // 有应答但不是 2xx（例如网关返 502）：服务器没到能接客的状态，按不通算。
    return { id: s.id, ok: false, rtt: -1 }
  } catch {
    // 走到这里分不清是「服务器挂了」还是「浏览器拦了跨域」—— CORS 失败在 fetch 里
    // 同样是 TypeError。所以再用 no-cors 复核一次：响应是 opaque（读不到状态码和
    // 响应体），但能 resolve 就说明 TCP/TLS/HTTP 全通。本地联调时页面来源是
    // localhost:5173，不在白名单里，全靠这一步才不至于「看起来所有线路都挂了」。
    try {
      const r = await fetch(url, { signal: ctl.signal, cache: 'no-store', mode: 'no-cors' })
      const ok = r.ok || r.type === 'opaque'
      return { id: s.id, ok, rtt: ok ? Math.round(performance.now() - t0) : -1 }
    } catch {
      return { id: s.id, ok: false, rtt: -1 }
    }
  } finally {
    clearTimeout(timer)
  }
}

/**
 * 选线（纯函数）：钉住的线路通就用它；否则按列表顺序取第一条通的；都不通返回 null。
 * `exclude` 用于失效切换时跳过刚失败的那条。
 *
 * 按列表顺序而不是按 rtt 快慢取，是为了让同一个班尽量落在同一台机器上 ——
 * SFU 有房间亲和性，同房间的人分到两台机器就互相看不见。
 *
 * 满载线路（rooms 接近 maxRooms）在还有别的线路可用时让位；如果所有通的线路都满了，
 * 仍然返回顺序最前的那条 —— 挤进去再被服务端拒，也好过客户端自己判定「无线路可用」。
 * 钉住的线路不受满载影响：手动钉住是明确意图，替他改掉会更难排查。
 */
export function choose(
  list: ServerInfo[], probes: ProbeResult[], pinned: string | null = null, exclude: string | null = null,
): ServerInfo | null {
  const byId = new Map(probes.map((p) => [p.id, p]))
  const usable = (s: ServerInfo) => byId.get(s.id)?.ok === true && s.id !== exclude
  if (pinned && pinned !== exclude && byId.get(pinned)?.ok === true) {
    return list.find((s) => s.id === pinned) ?? null
  }
  const free = list.find((s) => usable(s) && !isSaturated(byId.get(s.id)?.load))
  return free ?? list.find(usable) ?? null
}
